import React, {
  useCallback,
  useContext, useEffect, useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_AS_TYPE, BUTTON_TYPE } from '@motorway/mw-highway-code/enums';
import { Intent } from '@motorway/mw-highway-code/src/types';
import { Input } from '@THC/components/Input';

import cypressIds from 'CypressId';

import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { loginEmailUTMParams, SIGN_IN_PAGE_EVENTS } from 'Utilities/analytics';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import {
  composeValidators,
  emailValidator,
  getFieldState,
  requiredValidator,
} from 'Utilities/formValidators';
import { applyCypressData } from 'Utilities/index';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import { FeedbackTooltip } from 'Storybook/components/local/FeedbackTooltip/FeedbackTooltip';
import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import PageLoaderError from '../../../../components/transitions/PageLoaderError/PageLoaderError';
import LocalTexts from '../../../../texts.json';
import { EmailSent } from '../emailSent/EmailSent';
import { InputFieldWrapper } from '../form/InputFieldWrapper/InputFieldWrapper';
import { SignInFailed } from '../signInFailed/SignInFailed';

import { useSignInAction } from './hooks/useSignInAction';

import styles from './SignIn.scss';

const LocalT = new MDText(LocalTexts);

type SignInProps = {
  carValueTracker?: boolean;
  email?: string;
  onTrackerEmailSuccess?: () => void;
  subTitle?: string;
};

export const SignIn = ({
  carValueTracker,
  email: initialEmail,
  onTrackerEmailSuccess,
  subTitle,
}: SignInProps) => {
  const { vehicleLookupActions: { update }, vehicleLookupState } = useContext(VehicleLookupContext);
  const { userActions, userState: user } = useContext(UserContext);
  const [emailSent, setEmailSent] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<Record<string, unknown> | null>(null);

  const location = useLocation();
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const isSignInRoute = location.pathname.includes('/sign-in');

  const existingVehicle = vehicleLookupState?.owned;

  const apiErrorMessage = 'No such seller.';

  const onSubmitSuccess = useCallback((email: string) => setEmailSent(email), []);

  const onSubmitError = useCallback((email: string) => {
    if (existingVehicle && !isSignInRoute) {
      userActions.update({ email });
      update({ owned: false });
    }
  }, [existingVehicle, isSignInRoute, update, userActions]);

  const getRedirectTo = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get('redirect');

    if (redirect) {
      const fullRedirectURL = new URL(redirect, globalThis?.location?.origin ?? 'https://motorway.co.uk');
      const redirectParams = new URLSearchParams(fullRedirectURL.search);

      loginEmailUTMParams.forEach(([key, value]) => redirectParams.set(key, value));
      return `${fullRedirectURL.pathname}?${redirectParams.toString()}`;
    }

    const buildParams = new URLSearchParams(loginEmailUTMParams);
    if (vehicleLookupState?.enquiry?.mileage) {
      buildParams.set('mileage', vehicleLookupState.enquiry.mileage?.toString());
    }

    const vrm = vehicleLookupState?.vrm || '';
    return `/${vrm}?${buildParams.toString()}`;
  }, [location.search, vehicleLookupState?.enquiry?.mileage, vehicleLookupState?.vrm]);

  const { signInAction } = useSignInAction({ getRedirectTo, onSubmitError, onSubmitSuccess, setApiError });

  useEffect(() => {
    OPTIMIZELY_TRACK(EVENT.SIGN_IN_LOADED);
  }, [OPTIMIZELY_TRACK]);

  useEffect(() => {
    SIGN_IN_PAGE_EVENTS.SIGN_IN_LOADED({ isCarValueTracker: carValueTracker, isSignInRoute });
  }, [carValueTracker, isSignInRoute]);

  useEffect(() => {
    if (apiError?.message === apiErrorMessage && carValueTracker) {
      update({ showTrackerSignUp: true });
    }
  }, [apiError, carValueTracker, update]);

  useEffect(() => {
    if (emailSent) {
      onTrackerEmailSuccess?.();
    }

    if ((apiError && apiError?.message !== apiErrorMessage) || emailSent) {
      SIGN_IN_PAGE_EVENTS.SIGN_IN_CTA_CLICKED({ success: !!emailSent });
    }
  }, [apiError, emailSent, isSignInRoute, onTrackerEmailSuccess]);

  const onSubmit = async ({ email }: { email: string }) => {
    setLoading(true);

    OPTIMIZELY_TRACK(EVENT.SIGN_IN_CTA_CLICKED);

    await signInAction(email);

    setLoading(false);
  };

  const goBack = () => {
    setApiError(null);
  };

  if (loading) {
    return (
      <ComponentContent maxWidth={CONTENT_SIZES.small}>
        <div className={styles.loading}>
          <LoadingArrow />
        </div>
      </ComponentContent>
    );
  }

  if (emailSent) {
    return <EmailSent email={emailSent} />;
  }

  if (apiError) {
    if (apiError.message === apiErrorMessage && !carValueTracker) {
      return <SignInFailed onBackClick={goBack} />;
    }

    if (apiError.message !== apiErrorMessage) {
      return <PageLoaderError />;
    }
  }

  return (
    <div data-main-override className={styles.component}>
      <ComponentContent maxWidth={CONTENT_SIZES.big}>
        <h2>
          {isSignInRoute
            ? LocalT.translate('signInView.title')
            : LocalT.translate('warmSignInView.title')}
        </h2>
        {subTitle && (<p className={styles.subTitle}>{subTitle}</p>)}
      </ComponentContent>
      <ComponentContent maxWidth={CONTENT_SIZES.small}>
        <Form
          initialValues={{ email: initialEmail || user?.email }}
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <fieldset>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  validate={composeValidators(
                    requiredValidator(
                      LocalT.translate('components.emailField.errorMessage.default'),
                    ),
                    emailValidator(
                      LocalT.translate('components.emailField.errorMessage.default'),
                    ),
                  )}
                >
                  {({ input, meta, ...props }) => (
                    <InputFieldWrapper>
                      <FeedbackTooltip appendTo='parent' content={meta.error} intent={getFieldState(meta)} zIndex={9}>
                        <Input
                          fullWidth
                          showLabel
                          formProps={{
                            input: {
                              ...applyCypressData(cypressIds.fields.emailField),
                              ...input,
                              placeholder: LocalT.translate('components.emailField.placeholder'),
                            },
                          }}
                          id={props.id}
                          intent={getFieldState(meta) as Intent}
                          label={LocalT.translate('components.emailField.label')}
                        />
                      </FeedbackTooltip>
                    </InputFieldWrapper>
                  )}
                </Field>
              </fieldset>
              <div className={styles.buttons}>
                <Button
                  fullWidth
                  as={BUTTON_AS_TYPE.BUTTON}
                  data-cy='signInButton'
                  icon='chevron'
                  label={isSignInRoute
                    ? LocalT.translate('signInView.buttonLabel')
                    : LocalT.translate('warmSignInView.buttonLabel')}
                  loading={submitting}
                  type={BUTTON_TYPE.SUBMIT}
                />
              </div>
            </form>
          )}
        />
      </ComponentContent>
    </div>
  );
};
